import { type OperationalState } from "../constants/operational-states.ts"

/**
 * Mirrors `REQUIRED_FIELDS_BY_STATE` in
 * `ezbuild_back/web_app/apps/crm/operational_states.py`. The labels are the
 * backend's own, so a client-side miss and a server-side rejection read the
 * same.
 *
 * No overlay demands anything today. Follow-Up used to demand an owner, a
 * date and a time, which meant a lead could not be flagged for chasing
 * before the chase was booked. Scheduling still raises the flag by itself;
 * the flag alone books nothing.
 */
const REQUIRED_FIELDS_BY_STATE: Partial<
  Record<OperationalState, { name: string; label: string }[]>
> = {}

const MISSING_FIELDS_PREFIX =
  "Operational state update failed — missing mandatory fields: "

/**
 * The states a submission is *adding*, which is the only set the backend
 * validates. Clients echo the whole record back on update, so demanding the
 * states already on the lead would fail an unrelated edit to any lead whose
 * overlay predates the rule.
 */
function addedStates(
  next: OperationalState[] | null | undefined,
  current: OperationalState[] | null | undefined
): OperationalState[] {
  const already = new Set(current ?? [])
  return (next ?? []).filter((state) => !already.has(state))
}

function missingFieldsForOperationalStates(
  states: OperationalState[],
  resolve: (fieldName: string) => unknown
): string[] {
  const missing: string[] = []
  for (const state of states) {
    for (const { name, label } of REQUIRED_FIELDS_BY_STATE[state] ?? []) {
      if (!resolve(name) && !missing.includes(label)) missing.push(label)
    }
  }
  return missing
}

function fieldsRequiredByStates(states: OperationalState[]): Set<string> {
  const names = new Set<string>()
  for (const state of states) {
    for (const { name } of REQUIRED_FIELDS_BY_STATE[state] ?? []) {
      names.add(name)
    }
  }
  return names
}

export {
  MISSING_FIELDS_PREFIX,
  REQUIRED_FIELDS_BY_STATE,
  addedStates,
  fieldsRequiredByStates,
  missingFieldsForOperationalStates
}
